/*
 * Frontend implementation of a custom axios instance
 */
import axios from "axios";
import { auth } from "firebase.config";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { SERVER_BASE_URL } from "./helpers";

/**
 * Flag to track if we're currently restoring the session
 */
let isRestoringSession = false;

/**
 * Attempts to restore Firebase session from stored custom token
 */
const restoreSessionFromToken = async (): Promise<boolean> => {
  if (isRestoringSession) {
    // Wait for ongoing restoration
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!isRestoringSession) {
          clearInterval(checkInterval);
          resolve(!!auth.currentUser);
        }
      }, 100);
    });
  }

  const storedToken = sessionStorage.getItem("onboardingToken");
  if (!storedToken) {
    return false;
  }

  isRestoringSession = true;
  try {
    await signInWithCustomToken(auth, storedToken);
    return true;
  } catch (err) {
    // Token might be expired, clear it
    sessionStorage.removeItem("onboardingToken");
    return false;
  } finally {
    isRestoringSession = false;
  }
};

/**
 * Waits for Firebase auth to be ready and returns the current user.
 * This function creates a fresh promise each time to handle auth state changes.
 * If no user is found, attempts to restore the session from stored token.
 */
const waitForAuthUser = (): Promise<typeof auth.currentUser> => {
  return new Promise((resolve) => {
    // If user is already available, resolve immediately
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    // Otherwise wait for auth state to be determined
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (user) {
        resolve(user);
        return;
      }

      // No user found, try to restore session from stored token
      const restored = await restoreSessionFromToken();
      resolve(restored ? auth.currentUser : null);
    });

    // Also resolve after a timeout to prevent blocking indefinitely
    setTimeout(async () => {
      if (!auth.currentUser) {
        // Last attempt: try to restore session
        await restoreSessionFromToken();
      }
      resolve(auth.currentUser);
    }, 3000);
  });
};

const axiosClient = (() => {
  const getAuthToken = async () => {
    try {
      // Wait for Firebase auth to be ready and get the user
      const user = await waitForAuthUser();

      if (user) {
        const token = await user.getIdToken();
        return `Bearer ${token}`;
      }

      return undefined;
    } catch (err) {
      console.error("getAuthToken error:", err);
      return undefined;
    }
  };

  const instance = axios.create({
    baseURL: SERVER_BASE_URL
  });

  instance.interceptors.request.use(async (config) => {
    config.headers.Authorization = await getAuthToken();
    return config;
  });

  return instance;
})();

export default axiosClient;
